<template>
  <v-app-bar
    color="white"
    elevation="0"
    height="54"
    class="border-bottom d-flex justify-space-between px-4"
  >
    <div class="d-flex flex-shrink-0">
      <v-app-bar-nav-icon
        v-if="sidebarIsShown"
        width="36"
        height="36"
        class="global-cursor-pointer rounded-lg"
        @click.stop="sidebarHandler()"
      >
        <v-icon size="20" color="primary"> $burger-menu </v-icon>
      </v-app-bar-nav-icon>
      <v-app-bar-nav-icon
        v-else
        width="36"
        height="36"
        class="global-cursor-pointer rounded-lg"
        @click.stop="routeBack()"
      >
        <v-icon size="20" class="icon-rotate" color="primary"> $chevron-left </v-icon>
      </v-app-bar-nav-icon>
    </div>
    <div class="flex-shrink-0 global-cursor-pointer">
      <v-img
        class="mx-4"
        :width="isMdAndUp ? 140 : 32"
        :max-width="isMdAndUp ? 140 : 32"
        height="32"
        :src="getLogo"
        @click="redirectToDashboard()"
      />
    </div>
    <v-tooltip
      v-if="wrappedInfoButtonIsShown"
      location="bottom"
      color="white"
      class="mr-2"
      open-on-click
    >
      <template #activator="{ props }">
        <c-btn
          v-if="!isSmAndUp"
          class="px-0 border-info-500"
          variant="outlined"
          size="small"
          height="32"
          color="info-500"
          :icon-props="{
            icon: '$info',
            size: 20,
            class: 'mr-n1 mr-0 mr-sm-1'
          }"
          v-bind="props"
        />
      </template>
      <div class="d-flex flex-column">
        <div class="d-flex align-center pa-2 mb-1">
          <v-icon size="20" color="primary"> $check-outlined </v-icon>
          <span class="ml-3 font-weight-bold text-sm-caption text-truncate text-primary">
            {{ $t('main.full_manage.full_managed_title') }}
          </span>
        </div>
        <div class="d-flex align-center pa-2">
          <v-icon size="20" color="error"> $warning </v-icon>
          <span class="ml-3 font-weight-bold text-caption text-truncate text-error">
            Impersonate
          </span>
        </div>
      </div>
    </v-tooltip>

    <div class="d-flex align-center ga-4">
      <server-time class="d-none d-md-flex" />
      <v-tooltip
        v-if="isImpersonating && !wrappedInfoButtonIsShown"
        location="bottom"
        color="white"
        open-on-click
        :disabled="isLgAndUp"
      >
        <template #activator="{ props }">
          <c-btn
            class="px-0 border-error text-error px-lg-3"
            variant="outlined"
            size="small"
            height="32"
            color="error"
            :label="isLgAndUp ? 'Impersonate' : null"
            :icon-props="{
              icon: '$warning',
              size: 20,
              class: 'mr-n1 mr-lg-2'
            }"
            v-bind="props"
          />
        </template>
        <span v-if="!isLgAndUp" class="ml-1 text-black"> Impersonate </span>
      </v-tooltip>
      <v-tooltip
        v-if="isBeta && fullManageIsApproved && !wrappedInfoButtonIsShown"
        location="bottom"
        color="white"
        open-on-click
        :disabled="isLgAndUp"
      >
        <template #activator="{ props }">
          <c-btn
            class="px-0 border-primary text-primary px-lg-3"
            variant="outlined"
            size="small"
            height="32"
            color="primary"
            :label="isLgAndUp ? `${$t('main.full_manage.full_managed_title')}` : null"
            :icon-props="{
              icon: '$check-outlined',
              size: 20,
              class: 'mr-n1 mr-lg-2'
            }"
            v-bind="props"
          />
        </template>
        <span v-if="!isLgAndUp" class="ml-1 text-black">
          {{ $t('main.full_manage.full_managed_title') }}
        </span>
      </v-tooltip>
    </div>
    <div class="d-flex align-center justify-space-between ml-auto">
      <div class="d-flex align-center">
        <c-btn
          v-if="adFundsIsShown"
          :size="!isSmAndUp ? 'small' : 'default'"
          height="32px"
          color="primary"
          class="mr-1 mr-md-4 px-3"
          :to="{ name: routeNames.ADD_FUNDS }"
          :label="isSmAndUp ? $t('main.sidebar.add_funds') : null"
          :icon-props="{
            icon: '$wallet',
            size: 24,
            class: 'mr-n1 mr-sm-0'
          }"
        />
        <user-balance v-if="isMdAndUp" />
      </div>

      <v-spacer class="d-flex d-sm-none" />

      <div class="d-flex align-center ml-3 ml-lg-7">
        <router-link
          v-if="adNetworkHasSectionNotifications"
          :to="{ name: routeNames.NOTIFICATION }"
          :class="['mr-2 pa-1', { 'unread-notifications': isShowNotificationBell }]"
        >
          <c-icon size="24" name="$bell" color="black" />
        </router-link>
        <dropdown-menu />
      </div>
    </div>
  </v-app-bar>
</template>

<script>
  import { mapState, mapActions } from 'pinia'
  import { CBtn, CIcon } from '@clickadilla/components/components'
  import { datatableFormatter } from '@clickadilla/components/utils'
  import notificationsRepository from '@/services/repositories/notifications-repository'
  import ServerTime from '@/components/main/ServerTime.vue'
  import UserBalance from '@/components/main/UserBalance.vue'
  import DropdownMenu from '@/components/main/DropdownMenu.vue'
  import guardSections from '@/types/guard-sections'
  import handleErrors from '@/services/handleErrors'
  import routeNames from '@/types/route-names'
  import { useSettingsStore } from '@/store/settings/settings'
  import { usePersonalStore } from '@/store/personal/personal'
  import { useNotificationStore } from '@/store/notification/notification'

  export default {
    name: 'Toolbar',
    components: {
      CIcon,
      ServerTime,
      UserBalance,
      DropdownMenu,
      CBtn
    },
    props: {
      sidebarIsShown: {
        type: Boolean,
        required: true
      }
    },
    data() {
      return {
        routeNames,
        options: {
          page: 1,
          itemsPerPage: 0
        }
      }
    },
    computed: {
      ...mapState(useSettingsStore, [
        'isSidebarShown',
        'isMiniSidebarShown',
        'adNetwork',
        'getAdNetworkImages',
        'getMainRouteName'
      ]),
      ...mapState(usePersonalStore, ['fullManageStatus']),
      ...mapState(useNotificationStore, ['unreadNotifications']),
      wrappedInfoButtonIsShown() {
        return this.$vuetify.display.xs && this.isImpersonating && this.fullManageIsApproved
      },
      isSmAndUp() {
        return this.$vuetify.display.smAndUp
      },
      isLgAndUp() {
        return this.$vuetify.display.lgAndUp
      },
      isMdAndUp() {
        return this.$vuetify.display.mdAndUp
      },
      getLogo() {
        return this.isMdAndUp
          ? this.getAdNetworkImages.theme_white_logo
          : this.getAdNetworkImages.small_logo_dark
      },
      isShowNotificationBell() {
        return !!this.unreadNotifications
      },
      isImpersonating() {
        return !!this.$auth.user?.is_impersonating
      },
      isBeta() {
        return window.localStorage.getItem('isBeta')
      },
      fullManageIsApproved() {
        return this.fullManageStatus === 'approved'
      },
      adFundsIsShown() {
        let isShown = true
        if (!(this.adNetworkHasSectionAddFunds && this.isImpersonating)) {
          isShown = this.adNetworkHasSectionAddFunds
        }
        return isShown
      },
      adNetworkHasSectionAddFunds() {
        return this.adNetwork.guardSections.some(({ name }) => name === guardSections.ADD_FUNDS)
      },
      adNetworkHasSectionNotifications() {
        return this.adNetwork.guardSections.some(({ name }) => name === guardSections.NOTIFICATIONS)
      },
      isMobile() {
        return this.$vuetify.display.xs
      }
    },
    created() {
      if (this.adNetworkHasSectionNotifications) {
        this.fetchNotifications()
      }
    },
    methods: {
      ...mapActions(useSettingsStore, ['setIsSidebarShown', 'setIsMiniSidebarShown']),
      ...mapActions(useNotificationStore, ['setUnreadNotifications']),
      redirectToDashboard() {
        if (this.$route.name === this.getMainRouteName) return

        this.$router.push({ name: this.getMainRouteName })
      },
      sidebarHandler() {
        this.setIsSidebarShown(!this.isSidebarShown)
      },
      async fetchNotifications() {
        try {
          const params = datatableFormatter({
            options: this.options
          })
          const repository = await notificationsRepository.fetchNotifications(params)
          this.setUnreadNotifications(repository.unread_notifications)
        } catch (error) {
          handleErrors(error)
        }
      },
      routeBack() {
        if (window.history.state && window.history.length > 1) {
          this.$router.back()
        } else {
          this.$router.replace({ name: routeNames.MAIN })
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  .v-application .border-bottom {
    border-bottom: 1px solid rgb(var(--v-theme-secondary-200)) !important;
  }
  .icon-rotate {
    transform: rotate(-90deg);
  }
  .unread-notifications {
    position: relative;

    &:after {
      content: '';
      position: absolute;
      top: 3px;
      right: 3px;
      height: 13px;
      width: 13px;
      background-color: rgb(var(--v-theme-primary));
      border: 2px solid rgb(var(--v-theme-white));
      border-radius: 50%;
    }
  }
</style>
